import classNames from "classnames";
import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";

import { BUNDLE_ONLY_LICENSES } from "components/licenses/bundles";
import { License } from "components/licenses/delivery-history/DeliveryFormContent";
import { TenantsLicenses } from "components/tenants/edit-tenant/EditTenantView";
import { ComputedLicenses, DeliveryType, MIN_DATE } from "domain/licenses";
import { CombinedTier, LicensingModel } from "domain/tenants";
import { featureFlagService, FLAG_ASSET_MANAGEMENT, FLAG_FILE_SHARING } from "services/feature/FeatureFlagService";
import { ProductRateList } from "services/licenses/LicenseService";
import { tierLicenseService } from "services/licenses/TierLicenseService";
import { getCurrentTenantDetails } from "services/tenants/tenantCookieService";
import form from "styles/form.scss";
import { isExpired } from "utils/format";
import { logger } from "utils/logging";

import testIds from "testIds.json";

export const BDE_WORKFLOW_LICENSE_ID = "feature-workflow-bde";
export const BMDE_WORKFLOW_LICENSE_ID = "feature-workflow-bmde";
export const LUN_WORKFLOW_LICENSE_ID = "feature-workflow-lun";
export const BMP_WORKFLOW_LICENSE_ID = "feature-workflow-bmp";
export const FEATURE_CUSTOM_REPORT_VIEWS = "feature-custom-report-views";
export const FEATURE_LICENSE_CONSUMPTION = "feature-license-consumption";
export const FEATURE_CUSTOM_ROLES = "feature-custom-roles";
export const FEATURE_OVERVIEW_DASHBOARD = "feature-overview-dashboard";
export const FEATURE_PUBLIC_APIS = "feature-public-apis";
export const FEATURE_REPORTING = "feature-reporting";
export const FEATURE_REPORT_CUSTOMIZATION = "feature-report-customization";
export const FEATURE_ALLOW_REPORT_EXPORT_RESTRICTED_PATHS = "feature-allow-report-export-restricted-paths";
export const FEATURE_SSO = "feature-sso";
export const FEATURE_SUSTAINABILITY_DASHBOARD = "feature-sustainability-dashboard";
export const FEATURE_TENANT_MANAGEMENT = "feature-tenant-management";
export const FEATURE_USER_GROUPS = "feature-user-groups";
export const FEATURE_WORKFLOW_BDE = "feature-workflow-bde";
export const FEATURE_WORKFLOW_BMDE = "feature-workflow-bmde";
export const FEATURE_WORKFLOW_LUN = "feature-workflow-lun";
export const FEATURE_WORKFLOW_BMP = "feature-workflow-bmp";
export const FEATURE_LICENSE_POOLS = "feature-license-pools";
export const FEATURE_LICENSE_ALERTS = "feature-license-alerts";
export const FEATURE_ASSET_MANAGEMENT = "feature-asset-management";
export const FEATURE_FILE_SHARING = "feature-file-sharing";
export const FEATURE_MCP = "feature-mcp";
export const FEATURE_AI_AGENT = "feature-ai-agent";
export const BMS_LICENSE_TYPE_ALL = "bms-all";
export const BMS_LICENSE_TYPE_VALIDATION = "bms-validation";
export const BMS_LICENSE_TYPE_INSURANCE = "bms-insurance";
export const BMS_LICENSE_TYPE_LEASE = "bms-lease";
export const BMS_LICENSE_TYPE_NTF = "bms-ntf";
export const BMS_LICENSE_TYPE_BBTI = "bms-bbti";
export const BMS_LICENSE_FEATURE_ALL = "all";
export const BMS_LICENSE_FEATURE_VALIDATION = "Validation";
export const BMS_LICENSE_FEATURE_INSURANCE = "PreSalesInsurance";
export const BMS_LICENSE_FEATURE_LEASE = "Lease";
export const BMS_LICENSE_FEATURE_NTF = "FaultCheck";
export const BMS_LICENSE_FEATURE_RECOMMENDATIONS = "Recommendations";
export const BMS_LICENSE_FEATURE_BBTI = "TradeIn";
export const BMS_LICENSE_FEATURE_GATHER_IMEI = "gathering_imei";
export const BMS_LICENSE_FEATURE_IDENTIFICATION_VERIFICATION = "infogathering";
export const MOBILE_DIAGNOSTICS_ERASURE_LICENSE_REUSE_ID_AMOUNT = "32";
export const LICENSE_REUSE_PRO_AMOUNT = 72;
export const LICENSE_REUSE_PRO_SCALE_AMOUNT = 120;
export const DRIVE_ERASER_LICENSE_REUSE_ID = "5";
export const REUSE_LICENSE_IDS = [DRIVE_ERASER_LICENSE_REUSE_ID, MOBILE_DIAGNOSTICS_ERASURE_LICENSE_REUSE_ID_AMOUNT];

export const NON_DELIVERABLE_FEATURE_LICENSES = [
    FEATURE_PUBLIC_APIS,
    FEATURE_OVERVIEW_DASHBOARD,
    FEATURE_LICENSE_CONSUMPTION,
];
export const AI_LICENSES = [FEATURE_MCP, FEATURE_AI_AGENT];
export const BAM_ADDON_LICENSES = [FEATURE_ASSET_MANAGEMENT];

export const FEATURE_LICENSES = [
    FEATURE_CUSTOM_REPORT_VIEWS,
    FEATURE_LICENSE_CONSUMPTION,
    FEATURE_CUSTOM_ROLES,
    FEATURE_OVERVIEW_DASHBOARD,
    FEATURE_PUBLIC_APIS,
    FEATURE_REPORTING,
    FEATURE_REPORT_CUSTOMIZATION,
    FEATURE_ALLOW_REPORT_EXPORT_RESTRICTED_PATHS,
    FEATURE_SSO,
    FEATURE_SUSTAINABILITY_DASHBOARD,
    FEATURE_TENANT_MANAGEMENT,
    FEATURE_USER_GROUPS,
    FEATURE_WORKFLOW_BDE,
    FEATURE_WORKFLOW_BMDE,
    FEATURE_WORKFLOW_LUN,
    FEATURE_WORKFLOW_BMP,
    FEATURE_LICENSE_POOLS,
    FEATURE_LICENSE_ALERTS,
    FEATURE_FILE_SHARING,
    FEATURE_MCP,
    FEATURE_AI_AGENT,
];

export const OPPORTUNITY_ID_VALIDATION_FORMAT_1 = "006";
export const OPPORTUNITY_ID_VALIDATION_FORMAT_2 = "TEST";
export const SALESFORCE_LEAD_ID_VALIDATION_FORMAT_1 = "00Q";
export const SALESFORCE_CONTRACT_ID_VALIDATION_FORMAT_1 = "800";
export const SALESFORCE_ID_VALIDATION_FORMAT_1 = "001";
export const GENERIC_ID_LENGTH_FOR_SALESFORCE = 18;
export const GENERIC_ID_OPTION_FOR_SALESFORCE = "TEST";

export function getFeatureLicense() {
    const featureLicenses: Map<string | undefined, string[]> = new Map();
    featureLicenses.set("/workflows", ["FEATURE_WORKFLOW_BMDE", "FEATURE_WORKFLOW_BDE", "FEATURE_WORKFLOW_LUN"]);
    featureLicenses.set("/reports/all", ["FEATURE_REPORTING"]);
    featureLicenses.set("/dashboard/sustainability/own", ["FEATURE_SUSTAINABILITY_DASHBOARD"]);
    featureLicenses.set("/dashboard/overview/custom-overview", ["FEATURE_OVERVIEW_DASHBOARD"]);
    return featureLicenses;
}

export function getDeliveryTypes(inTenantCreation?: boolean): Map<string, string> {
    const { t } = useTranslation();
    if (inTenantCreation) {
        return new Map<DeliveryType, string>([["NEW_DEAL", t("DeliveryHistory.deliveryType.newDeal")]]);
    }
    return new Map<DeliveryType, string>([
        ["NEW_DEAL", t("DeliveryHistory.deliveryType.newDeal")],
        ["REIMBURSEMENT", t("DeliveryHistory.deliveryType.reimbursement")],
        ["SUPPLEMENTARY", t("DeliveryHistory.deliveryType.supplementary")],
        ["CORRECTION", t("DeliveryHistory.deliveryType.correction")],
        ["CONVERSION", t("DeliveryHistory.deliveryType.conversion")],
        ["TRIAL", t("DeliveryHistory.deliveryType.trial")],
    ]);
}

export function fetchDynamicTierLicenses(tier: string): string[] {
    try {
        return tierLicenseService.fetchTierLicenses(tier);
    } catch (error) {
        logger.error("Fetching tier licenses failed", error);
        return [];
    }
}

export function fetchDynamicTierAddOns(tier: string): string[] {
    return tierLicenseService.fetchTierAddOns(tier);
}

export function isAddOn(productId: string) {
    return tierLicenseService.fetchTierAddOns(getCurrentTenantDetails().tenantTier).includes(productId);
}

export function fetchDynamicTierMigratedToCommonLicenses(tier: string): string[] {
    return tierLicenseService.fetchTierMigratedToCommonLicenses(tier);
}

export function isMigratedToCommonLicense(productId: string) {
    return tierLicenseService
        .fetchTierMigratedToCommonLicenses(getCurrentTenantDetails().tenantTier)
        .includes(productId);
}

export function createLicenseTypes(
    includeOthers: boolean,
    includeLegacyWorkflowLicenses?: boolean,
    licensingModel?: LicensingModel
): { productName: string; productId: string }[] {
    const types = [
        { productName: "Drive Eraser - Asset Profiler", productId: "1" },
        { productName: "Mobile Diagnostics & Erasure - Device Image Validation", productId: "2" },
        { productName: "Drive Eraser - PC Edition (per drive)", productId: "3" },
        { productName: "Drive Eraser - License Reuse", productId: "5" },
        { productName: "Network Device Eraser - Erasure", productId: "6" },
        { productName: "Common License", productId: "7" },
        { productName: "Eraser for Apple Devices - Detailed Hardware Check", productId: "10" },
        { productName: "Removable Media Eraser - Enterprise Edition", productId: "11" },
        { productName: "Removable Media Eraser - Volume Edition (per drive)", productId: "12" },
        { productName: "Mobile Diagnostics & Erasure - Carrier ID Check", productId: "16" },
        { productName: "Mobile Diagnostics & Erasure - FMiP Check", productId: "17" },
        { productName: "Asset Reimaging", productId: "19" },
        { productName: "Drive Eraser - Volume Edition", productId: "20" },
        { productName: "Drive Eraser - Diagnostics", productId: "21" },
        { productName: "Mobile Diagnostics & Erasure - Carrier Insights Extension", productId: "24" },
        { productName: "LUN Eraser - Erasure (per GB)", productId: "26" },
        { productName: "File Eraser - Home Edition", productId: "27" },
        { productName: "File Eraser - Data Center Edition", productId: "28" },
        { productName: "Mobile Diagnostics & Erasure - Erasure", productId: "30" },
        { productName: "Mobile Diagnostics & Erasure - Asset Profiler", productId: "31" },
        { productName: "Mobile Diagnostics & Erasure - License Reuse", productId: "32" },
        { productName: "Mobile Diagnostics - Integration", productId: "33" },
        { productName: "Mobile Diagnostics & Erasure - Diagnostics", productId: "34" },
        { productName: "Mobile Diagnostics & Erasure - Activation Lock", productId: "35" },
        { productName: "Drive Eraser - Enterprise Volume Edition", productId: "36" },
        { productName: "Drive Verifier - Verification", productId: "37" },
        { productName: "LUN Eraser - Erasure (per erasure)", productId: "38" },
        { productName: "Virtual Machine Eraser - Erasure (per GB)", productId: "39" },
        { productName: "Virtual Machine Eraser - Erasure (per erasure)", productId: "40" },
        { productName: "Check For FeliCa", productId: "42" },
        { productName: "Mobile For FeliCa", productId: "43" },
        { productName: "File Eraser - Enterprise Edition", productId: "44" },
        { productName: "Mobile Diagnostics & Erasure - Enterprise Edition", productId: "46" },
        { productName: "Degausser", productId: "48" },
        { productName: "PreInstall", productId: "49" },
        { productName: "Drive Eraser - Enterprise Subscription Edition", productId: "50" },
        { productName: "On-Premise BMP", productId: "51" },
        { productName: "Cloud Storage Eraser - Enterprise Edition", productId: "62" },
        { productName: "Game Console Eraser", productId: "70" },
        { productName: "Mobile Workspace Edition - Asset Profiler", productId: "72" },
        { productName: "Mobile Workspace Edition - Erasure", productId: "73" },
        { productName: "Mobile Workspace Edition - Diagnostics", productId: "74" },
        { productName: "Eraser for Apple Devices - Asset Profiler", productId: "75" },
        { productName: "Eraser for Apple Devices - Volume Edition", productId: "76" },
        { productName: "Eraser for Apple Devices - Enterprise Volume Edition", productId: "77" },
        { productName: "Eraser for Apple Devices - Diagnostics", productId: "78" },
        { productName: "Mobile Diagnostics & Erasure - Carrier Insights", productId: "79" },
        { productName: "Autopilot Detection", productId: "80" },
        { productName: "Autopilot Detection - Subscription", productId: "81" },
        { productName: "Drive Eraser - OEM Asset Based Licensing", productId: "82" },
        { productName: "Autopilot Recheck", productId: "83" },
        { productName: "Technical Account Manager", productId: "90" },
        { productName: "Management Portal - Agentic Action", productId: "200" },
    ];

    if (includeOthers) {
        types.push(
            ...[
                {
                    productName: "Mobile Solutions - Validation",
                    productId: "bms-validation",
                },
                { productName: "Mobile Solutions - Buy-Back / Trade In", productId: "bms-bbti" },
                { productName: "Mobile Solutions - Insurance", productId: "bms-insurance" },
                { productName: "Mobile Solutions - My Device Health", productId: "bms-ntf" },
                { productName: "Mobile Solutions - Lease", productId: "bms-lease" },
                { productName: "Mobile Solutions - All Features", productId: "bms-all" },
                { productName: "Workflows - Drive Eraser", productId: BDE_WORKFLOW_LICENSE_ID },
                { productName: "Workflows - Mobile Diagnostics & Erasure", productId: BMDE_WORKFLOW_LICENSE_ID },
                { productName: "Workflows - Blancco LUN Eraser", productId: LUN_WORKFLOW_LICENSE_ID },
                { productName: "Workflows - Blancco Management Portal", productId: BMP_WORKFLOW_LICENSE_ID },
                {
                    productName: "Management Portal - Sustainability dashboard",
                    productId: FEATURE_SUSTAINABILITY_DASHBOARD,
                },
                {
                    productName: "Management Portal - Overview dashboard",
                    productId: FEATURE_OVERVIEW_DASHBOARD,
                },
                { productName: "Management Portal - Public APIs", productId: FEATURE_PUBLIC_APIS },
                { productName: "Management Portal - Reporting", productId: FEATURE_REPORTING },
                { productName: "Mobile Software Development Kit (SDK)", productId: "usdk" },
                { productName: "Management Portal - Report customization", productId: FEATURE_REPORT_CUSTOMIZATION },
                {
                    productName: "Management Portal - Allow restricted paths",
                    productId: FEATURE_ALLOW_REPORT_EXPORT_RESTRICTED_PATHS,
                },
                { productName: "Management Portal - Custom report views", productId: FEATURE_CUSTOM_REPORT_VIEWS },
                { productName: "Management Portal - Custom roles", productId: FEATURE_CUSTOM_ROLES },
                { productName: "Management Portal - Single Sign-On (SSO)", productId: FEATURE_SSO },
                { productName: "Management Portal - Tenant management", productId: FEATURE_TENANT_MANAGEMENT },
                { productName: "Management Portal - User groups", productId: FEATURE_USER_GROUPS },
                { productName: "Management Portal - License pools", productId: FEATURE_LICENSE_POOLS },
                { productName: "Management Portal - License alerts", productId: FEATURE_LICENSE_ALERTS },
                { productName: "Management Portal - MCP Server", productId: FEATURE_MCP },
                { productName: "Management Portal - AI agent", productId: FEATURE_AI_AGENT },
                ...(featureFlagService.isFeatureEnabled(FLAG_ASSET_MANAGEMENT)
                    ? [{ productName: "Management Portal - Asset management", productId: FEATURE_ASSET_MANAGEMENT }]
                    : []),
                ...(featureFlagService.isFeatureEnabled(FLAG_FILE_SHARING)
                    ? [{ productName: "Management Portal - File sharing", productId: FEATURE_FILE_SHARING }]
                    : []),
            ]
        );

        if (includeLegacyWorkflowLicenses) {
            const legacyWorkflowTypes: { productName: string; productId: string }[] = [];
            createIbrLicenseTypes().forEach((each) => {
                const workflowProduct = types.find((eachType) => each.productName === eachType.productId);
                if (workflowProduct) {
                    legacyWorkflowTypes.push({ productId: each.productId, productName: workflowProduct.productName });
                }
            });
            types.push(...legacyWorkflowTypes);
        }
    }

    if (licensingModel === LicensingModel.OLD_MODEL) {
        return types.filter((type) => !BUNDLE_ONLY_LICENSES.includes(type.productId));
    }

    return types;
}

export function createIbrLicenseTypes(): { productId: string; productName: string }[] {
    return [
        { productId: "14", productName: BMDE_WORKFLOW_LICENSE_ID },
        { productId: "15", productName: BDE_WORKFLOW_LICENSE_ID },
        { productId: "41", productName: LUN_WORKFLOW_LICENSE_ID },
    ];
}

export function createProductIdToNameIbrMap(): Map<string, string> {
    const licenses = createIbrLicenseTypes();
    return new Map<string, string>(licenses.map((each) => [each.productName, each.productId]));
}

export function createProductIdToNameMap(): Map<string, string> {
    const licenses = createLicenseTypes(true, false);
    return new Map<string, string>(licenses.map((each) => [each.productId, each.productName]));
}

export function createSortedLicenseTypes(): { productName: string; productId: string }[] {
    const licenses = createLicenseTypes(true, false);
    return licenses.sort((a, b) => a.productName.localeCompare(b.productName));
}

export function isSubscriptionLicense(type: string): boolean {
    return ["46", "50", "51", "62", "81", "90"].includes(type);
}

// Entitlement creation is restricted for the following license types
export const RESTRICTED_ENTITLEMENT_LICENSE_TYPES = [
    "7",
    "22",
    "39",
    "45",
    "47",
    "49",
    "51",
    "72",
    "73",
    "74",
    "76",
    "77",
    "80",
    "81",
    "83",
];

export const HL_ONLY_ENTITLEMENT_LICENSE_TYPES = ["4", "8", "9", "13", "22", "25", "33", "41", "42", "43", "48"];

export function toDate(date: Date | string): Date {
    if (typeof date === "string") {
        return new Date(date);
    }
    return date;
}

export const isExpirationDateValid = (expiration: Date | string, maxDate: Date | string): boolean => {
    const expirationDate = toDate(expiration);
    const maxDateAdjusted = toDate(maxDate);
    maxDateAdjusted.setDate(maxDateAdjusted.getDate() + 1);
    return expirationDate >= MIN_DATE && expirationDate < maxDateAdjusted;
};

export const isAmountValid = (item: License, usedCount?: number, inLicensePool?: boolean): boolean => {
    return inLicensePool ? (usedCount ? item.licensesToAdd >= usedCount : true) : true;
};

export const isAmountAvailable = (item: License, inLicensePool?: boolean): boolean => {
    return item.licensesToAdd <= deduceErrorThreshold(item, inLicensePool);
};

function deduceErrorThreshold(license: License, inLicensePool?: boolean) {
    return inLicensePool
        ? license.available
        : license.deliveryMode === "SET"
          ? license.available + license.parentAvailableAmount
          : license.parentAvailableAmount;
}

export function deduceAddOnsBasedOnBundle(tier: CombinedTier) {
    return fetchDynamicTierAddOns(tier);
}

export function computeBundleAddOnLicenses(
    allTenantLicenses: TenantsLicenses[],
    tierLicenses: string[] | undefined,
    tier: CombinedTier
): ComputedLicenses[] {
    return deduceAddOnsBasedOnBundle(tier)
        .map((addOnType) => {
            const sum = allTenantLicenses
                .filter(
                    (license) =>
                        license.productId === addOnType &&
                        !isExpired(license.expirationDate) &&
                        tierLicenses?.includes(license.productId)
                )
                .reduce((sum, license) => sum + license.available, 0);
            return {
                licenseId: addOnType,
                amount: sum,
            };
        })
        .filter((license) => license.amount !== 0);
}

export function computeMigratedToCommonLicenses(
    allTenantLicenses: TenantsLicenses[],
    tierLicenses: string[] | undefined,
    newTier: CombinedTier
): ComputedLicenses[] {
    return fetchDynamicTierMigratedToCommonLicenses(newTier)
        .map((productId) => {
            const sum = allTenantLicenses
                .filter(
                    (license) =>
                        license.productId === productId &&
                        !isExpired(license.expirationDate) &&
                        tierLicenses?.includes(license.productId)
                )
                .reduce((sum, license) => sum + license.available, 0);
            return {
                licenseId: productId,
                amount: sum,
            };
        })
        .filter((license) => license.amount !== 0);
}

export function computeEquivalentOfCommonLicenses(
    allTenantLicenses: TenantsLicenses[],
    productToRateList: ProductRateList[],
    deducedTierLicenses: string[] | undefined
) {
    return Math.round(
        allTenantLicenses.reduce((total, license) => {
            if (!deducedTierLicenses || !productToRateList || isExpired(license.expirationDate)) {
                return total;
            }

            const rate = productToRateList.find(
                (rate) => rate.productId === license.productId && deducedTierLicenses.includes(license.productId)
            )?.rate;

            return total + (rate ? license.remainingLicenses * rate : 0);
        }, 0)
    );
}

export const displayAmountErrorLabel = (
    license: License,
    t: TFunction,
    inLicensePool?: boolean,
    usedLicenseCountInPool?: number,
    licensesToAddTestIds?: string
) => {
    return isNaN(license.licensesToAdd) ? (
        <div
            className={form.error}
            data-testid={
                licensesToAddTestIds ??
                testIds.workArea.license.deliveryHistory.createDeliveryDialog.assignedAmountInput.errorLabel
            }
        >
            <span>{t("DeliveryHistory.addLicenseDelivery.validation.licensesToAdd")}</span>
        </div>
    ) : isAmountValid(license, usedLicenseCountInPool, inLicensePool) ? (
        isAmountAvailable(license, inLicensePool ?? inLicensePool) ? null : (
            <div
                className={classNames(form.error, form.errorMultiLine)}
                data-testid={
                    inLicensePool
                        ? testIds.workArea.license.licensePools.manageLicensePoolView.table.assignedAmountInput
                              .errorLabel
                        : testIds.workArea.license.deliveryHistory.createDeliveryDialog.assignedAmountInput.errorLabel
                }
            >
                <span>
                    {t("DeliveryHistory.addLicenseDelivery.validation.tooManyLicenses", {
                        threshold: deduceErrorThreshold(license, inLicensePool),
                    })}
                </span>
            </div>
        )
    ) : (
        <div className={form.error}>
            <span>
                {t("DeliveryHistory.addLicenseDelivery.validation.lessThanUsedLicenses", {
                    used: usedLicenseCountInPool,
                })}
            </span>
        </div>
    );
};

export const bmsLicenseTypesAndFeatureKeys = [
    { type: BMS_LICENSE_TYPE_VALIDATION, feature: BMS_LICENSE_FEATURE_VALIDATION },
    { type: BMS_LICENSE_TYPE_LEASE, feature: BMS_LICENSE_FEATURE_LEASE },
    { type: BMS_LICENSE_TYPE_BBTI, feature: BMS_LICENSE_FEATURE_BBTI },
    { type: BMS_LICENSE_TYPE_INSURANCE, feature: BMS_LICENSE_FEATURE_INSURANCE },
    { type: BMS_LICENSE_TYPE_NTF, feature: BMS_LICENSE_FEATURE_NTF },
    { type: BMS_LICENSE_TYPE_NTF, feature: BMS_LICENSE_FEATURE_RECOMMENDATIONS },
];

export function showOnlyTokenLicense(licensingModel?: LicensingModel) {
    return licensingModel === LicensingModel.BUNDLE_WITH_TOKEN;
}

export const BLANCCO_TOKEN_ID = "7";

export function isCommonLicense(type: string) {
    return type === BLANCCO_TOKEN_ID;
}

export const OEM_LICENSE_ID = "82";

export const IBR_FEATURES = [FEATURE_WORKFLOW_BDE, FEATURE_WORKFLOW_BMDE, FEATURE_WORKFLOW_LUN];

export function isIbrFeature(licenseType: string) {
    return IBR_FEATURES.includes(licenseType);
}

export function isFeatureLicense(licenseType: string) {
    return licenseType.startsWith("feature");
}

export function isAutopilotLicense(licenseType: string) {
    return ["80", "81", "83"].includes(licenseType);
}

export const checkIbrLicensesArePresent = (type: string): boolean => {
    if (IBR_FEATURES.includes(type)) {
        return true;
    }
    return !type.startsWith("feature") || !isNaN(parseInt(type));
};

export interface SessionDurationOption {
    label: string;
    value: number;
}

export function getSessionDurationOptions(t: TFunction): SessionDurationOption[] {
    return [
        { label: t("Settings.sessionSettings.durations.thirtyMinutes"), value: 30 * 60 },
        { label: t("Settings.sessionSettings.durations.oneHour"), value: 60 * 60 },
        { label: t("Settings.sessionSettings.durations.eightHours"), value: 8 * 60 * 60 },
        { label: t("Settings.sessionSettings.durations.oneDay"), value: 24 * 60 * 60 },
        { label: t("Settings.sessionSettings.durations.sevenDays"), value: 7 * 24 * 60 * 60 },
        { label: t("Settings.sessionSettings.durations.fourteenDays"), value: 14 * 24 * 60 * 60 },
        { label: t("Settings.sessionSettings.durations.thirtyDays"), value: 30 * 24 * 60 * 60 },
    ];
}

export const OEM_LICENSE = createLicenseTypes(false).find((license) => license.productId == OEM_LICENSE_ID);
